/**
 * @fileoverview Update-notification-preferences mutation hook.
 * @description Wraps PUT /notifications/preferences. On success it refetches the preferences (the
 * returned promise keeps the mutation pending until the fresh rows, with their new rowVersions,
 * have landed). On a version conflict (409/6007) it refetches too, so the next save carries the
 * latest rowVersions; the drafts the user typed are kept by the caller.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { NOTIFICATION_PREFERENCES_QUERY_KEY } from '@/features/notifications/hooks/useNotificationPreferences'
import { updateNotificationPreferences } from '@/features/notifications/services/notification-preferences.service'
import { ApiException } from '@/shared/api/api.exception'
import { ERROR_CODES } from '@/shared/constants/error-codes.constants'

/**
 * @description Saves notification preference changes and keeps the preferences query current.
 * @returns {ReturnType<typeof useMutation>} The mutation object (mutate with the updates).
 */
export function useUpdateNotificationPreferences() {
  const queryClient = useQueryClient()

  /**
   * @description Refetches the preferences so rows and rowVersions match the server.
   */
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: NOTIFICATION_PREFERENCES_QUERY_KEY })

  return useMutation({
    mutationFn: updateNotificationPreferences,
    onSuccess: refresh,
    /**
     * @description Reloads the rows after a stale-version rejection; other errors need nothing.
     * @param {unknown} error - The rejection.
     */
    onError: (error: unknown) => {
      if (error instanceof ApiException && error.code === ERROR_CODES.VERSION_CONFLICT) {
        void refresh()
      }
    },
  })
}
